import * as z from 'zod'

import { CreateWidgetVars, UpdateWidgetVars } from '@/common/api/params/widget'
import { NameLabelMapping } from '@/common/app/message'
import {
  BUNDLE_LAYOUT_LOGICS,
  BUNDLE_MAX_ITEMS_MAX,
  BUNDLE_MAX_ITEMS_MIN
} from '@/common/app/modules/recommend/widget'
import { NumberValue, StringLength } from '@/common/form/schema'
import {
  Widget,
  WidgetContentType,
  WidgetDisplayLogic,
  WidgetLayoutType,
  WidgetStatus
} from '@/common/model/recommend/widget'

export const widgetSchema = z
  .object({
    id: z.number().readonly().optional(),
    siteId: z.number().readonly(),
    contentType: z.custom<WidgetContentType>(),
    name: z.string().min(1).max(StringLength.NORMAL_MAX),
    displayLogic: z.custom<WidgetDisplayLogic>(),
    // タイトルを利用するか（フォーム専用。不要時は送信時に title_template を空にする）
    useTitle: z.boolean(),
    titleTemplate: z.string().max(StringLength.NORMAL_MAX),
    layoutType: z.custom<WidgetLayoutType>(),
    maxItems: z.number().int().positive().max(NumberValue.STOCK_MAX),
    customCss: z.string().max(StringLength.TEXT_MAX),
    status: z.custom<WidgetStatus>(),
    widgetPageId: z.number().nullable()
  })
  // バンドルの指定条件（サイトがW2連動かはフォーム側で判定する。スキーマはサイト情報を持たないため）
  .superRefine((data, ctx) => {
    if (data.layoutType !== WidgetLayoutType.BUNDLE) return
    if (data.contentType !== WidgetContentType.PRODUCT) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['layoutType'],
        message: 'バンドルは商品レコメンドでのみ選べます'
      })
    }
    if (!BUNDLE_LAYOUT_LOGICS.includes(data.displayLogic)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['layoutType'],
        message: 'バンドルは「BB」「OB」「Similar」のロジックでのみ選べます'
      })
    }
    if (data.maxItems < BUNDLE_MAX_ITEMS_MIN || data.maxItems > BUNDLE_MAX_ITEMS_MAX) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['maxItems'],
        message: `バンドルの表示数は${BUNDLE_MAX_ITEMS_MIN}〜${BUNDLE_MAX_ITEMS_MAX}で選んでください`
      })
    }
  })

export type WidgetFormSchemaType = z.infer<typeof widgetSchema>

export const convertWidgetSchemaToVars = (
  schema: WidgetFormSchemaType
): UpdateWidgetVars | CreateWidgetVars => {
  const base = {
    name: schema.name,
    display_logic: schema.displayLogic,
    content_type: schema.contentType,
    title_template: schema.useTitle ? schema.titleTemplate : '',
    layout_type: schema.layoutType,
    max_items: schema.maxItems,
    custom_css: schema.customCss,
    status: schema.status,
    widget_page_id: schema.widgetPageId
  }
  if (schema.id !== undefined) {
    return { site_id: schema.siteId, widget_id: schema.id, ...base }
  }
  return { site_id: schema.siteId, ...base }
}

export const convertWidgetToSchema = (widget: Widget): WidgetFormSchemaType => {
  return {
    id: widget.id,
    siteId: widget.site_id,
    contentType: widget.content_type,
    name: widget.name,
    displayLogic: widget.display_logic,
    useTitle: !!widget.title_template,
    titleTemplate: widget.title_template ?? '',
    layoutType: widget.layout_type,
    maxItems: widget.max_items,
    customCss: widget.custom_css ?? '',
    status: widget.status,
    widgetPageId: widget.widget_page_id
  }
}

export const makeDefaultWidgetSchema = (siteId: number): WidgetFormSchemaType => ({
  siteId,
  contentType: WidgetContentType.PRODUCT,
  name: '',
  displayLogic: WidgetDisplayLogic.BB,
  useTitle: false,
  titleTemplate: '',
  layoutType: WidgetLayoutType.GRID,
  maxItems: 6,
  customCss: '',
  status: WidgetStatus.ACTIVE,
  widgetPageId: null
})

export const widgetNameLabelMapping: NameLabelMapping = {
  name: 'レコメンドパーツ名',
  displayLogic: '表示に使うレコメンドロジック',
  titleTemplate: '表示レコメンドタイトル',
  layoutType: 'レイアウト',
  maxItems: '表示数',
  customCss: 'カスタムCSS',
  status: 'ステータス',
  widgetPageId: '設置ページ'
}
